import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';

import { useImportCatalogStandardsMutation } from '../../standardsCatalogApi';
import { useCatalogCart } from '../../hooks/useCatalogCart';
import { STANDARDS_LIBRARY_PATH } from '../../paths';

import { DestinationPicker } from './DestinationPicker';

import type {
  CatalogImportSelection,
  ImportCatalogStandardsResult,
  UUID,
} from '@/shared/types/userStandard';
import { useAppSelector } from '@/shared/hooks/redux';

interface ImportDialogProps {
  open: boolean;
  ownerId: UUID;
  onClose: () => void;
}

type ImportMode = 'preserveHierarchy' | 'flattenLeaves';
type DuplicateAction = 'skip' | 'importCopy';

export function ImportDialog({ open, ownerId, onClose }: ImportDialogProps) {
  const navigate = useNavigate();
  const items = useAppSelector((state) => state.standardsCart.items);
  const { clear } = useCatalogCart();
  const [importStandards, { isLoading }] = useImportCatalogStandardsMutation();

  const [destination, setDestination] = useState<UUID | null>(null);
  const [mode, setMode] = useState<ImportMode>('preserveHierarchy');
  const [duplicateAction, setDuplicateAction] = useState<DuplicateAction>('skip');
  const [result, setResult] = useState<ImportCatalogStandardsResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selections = useMemo<CatalogImportSelection[]>(
    () =>
      Object.values(items).map((item) => ({
        id: item.node.id,
        withDescendants: item.withDescendants,
        excludedIds: item.excludedIds,
      })),
    [items]
  );

  const handleClose = () => {
    if (isLoading) return;
    setResult(null);
    setError(null);
    onClose();
  };

  const runImport = async () => {
    setError(null);
    try {
      const res = await importStandards({
        ownerId,
        targetParentId: destination,
        selections,
        mode,
        duplicateAction,
      }).unwrap();
      setResult(res);
    } catch {
      setError('Import failed. Please try again.');
    }
  };

  const finish = (goToLibrary: boolean) => {
    clear();
    setResult(null);
    onClose();
    if (goToLibrary) navigate(STANDARDS_LIBRARY_PATH);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      {result ? (
        <>
          <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 56 }} />
            <Typography variant="h6" sx={{ mt: 1 }}>
              {result.createdCount} standard{result.createdCount === 1 ? '' : 's'} imported
            </Typography>
            {result.skippedDuplicateCount > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {`${result.skippedDuplicateCount} duplicate${result.skippedDuplicateCount === 1 ? '' : 's'} skipped.`}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button onClick={() => finish(false)}>Import more</Button>
            <Button variant="contained" onClick={() => finish(true)}>
              View in My Standards
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>
            Import {selections.length} standard{selections.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Destination
                </Typography>
                <DestinationPicker
                  ownerId={ownerId}
                  value={destination}
                  onChange={setDestination}
                />
                {destination === null && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.75, display: 'block' }}
                  >
                    Pick a collection (or create one) to import into.
                  </Typography>
                )}
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={mode === 'flattenLeaves'}
                      onChange={(event) =>
                        setMode(event.target.checked ? 'flattenLeaves' : 'preserveHierarchy')
                      }
                    />
                  }
                  label="Add as a flat list"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 4, display: 'block' }}
                >
                  By default the source's groupings are kept. Check this to drop them and add just
                  the standards.
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Duplicates
                </Typography>
                <RadioGroup
                  value={duplicateAction}
                  onChange={(event) => setDuplicateAction(event.target.value as DuplicateAction)}
                >
                  <FormControlLabel value="skip" control={<Radio />} label="Skip duplicates" />
                  <FormControlLabel
                    value="importCopy"
                    control={<Radio />}
                    label="Import another copy"
                  />
                </RadioGroup>
              </Box>

              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={isLoading} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={runImport}
              disabled={isLoading || selections.length === 0 || destination === null}
            >
              {isLoading ? 'Importing…' : `Import ${selections.length}`}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default ImportDialog;
